import {Ajax} from '../core/ajax';
import {BackendError, CommonRepo, FieldError, ValidationRule} from '../core/common-repository';
import {Components} from '../core/components';
import {DOM} from '../core/dom';
import {Forms} from '../core/forms';
import {ReCaptcha} from '../core/recaptcha';
import {Select} from '../core/select';
import {Texts} from '../core/texts';
import {Validation} from '../core/validation';

const TYPES: Components.Radio[] = [
  {name: 'type-general', value: 'GENERAL', label: Texts.GENERAL},
  {name: 'type-freelancer', value: 'FREELANCER', label: Texts.FREELANCER},
  {name: 'type-company', value: 'COMPANY', label: Texts.COMPANY}
];

const modesMap = {
  general: 'GENERAL',
  freelancer: 'FREELANCER',
  company: 'COMPANY'
};

const url = window.location.search;
const searchParams = new URLSearchParams(url);
const mode = searchParams.get('mode');

const FORM_TEMPLATE = `
    <h3 class="no-margin full-row">${Texts.SEND_US_A_MESSAGE}</h3>
    <div class="full-row contact-types">
      ${Components.radio('type', TYPES, modesMap[mode] || 'GENERAL')}
    </div>
    ${Components.input('firstName', Texts.FIRST_NAME, Texts.FIRST_NAME)}
    ${Components.input('lastName', Texts.LAST_NAME, Texts.LAST_NAME)}
    ${Components.input('email', Texts.EMAIL, Texts.EMAIL, '', 'email')}
    ${Components.input('phone', Texts.PHONE, Texts.PHONE)}
    ${Components.input('company', Texts.COMPANY, Texts.COMPANY)}
    ${Components.select('country', Texts.COUNTRY, Texts.COUNTRY)}
    ${Components.textarea({ name: 'message', label: Texts.MESSAGE, placeholder: Texts.ENTER_YOUR_MESSAGE })}
    ${Components.checkbox('termsAccepted', Texts.TERMS_ACCEPTED_TNC)}
    <div class="full-row">
      <label id="contact-form-error" class="error"></label>
      <button id="contact-us" class="gigs-btn rainbow" type="button">${Texts.SEND}</button>
    </div>`;

const SUCCESS_TEMPLATE = `
    <div class="full-row">
      <h3 class="no-margin centered">${Texts.THANK_YOUR_FOR_YOUR_MESSAGE}</h3>
      <h4 class="centered">${Texts.WE_WILL_CONTACT_YOU_SOON}</h4>
    </div>`;

namespace Repo {
  export function send(contact: ContactInfo, token: string): Ajax.Callbacks<void, BackendError> {
    return Ajax.post<void, BackendError>('v1/contacts/submit', contact, [{name: 'X-Token', value: token}]);
  }
}

namespace Contact {
  const rules: {[type: string]: ValidationRule[]} = {
    CONTACT_GENERAL: [],
    CONTACT_COMPANY: [],
    CONTACT_FREELANCER: []
  };

  export function onload(): void {
    DOM.byId('contact-form').innerHTML = FORM_TEMPLATE;
    loadValidationRules('CONTACT_GENERAL');
    for (const type of TYPES) {
      DOM.byId(type.name).addEventListener('change', ev => {
        loadValidationRules('CONTACT_' + type.value);
      });
    }
    loadCountries();
    DOM.onIdClick<HTMLButtonElement>('contact-us', (_, btn) => submit(btn));
    document.body.classList.add('show-captcha');
  }

  function loadCountries(): void {
    CommonRepo.getCountries().onSuccess(countries => {
      Select.setup('country', countries.map(c => { return {label: c.name, value: c.countryISO3code}; }), false, Texts.COUNTRY);
    });
  }

  function loadValidationRules(type: string): void {
    if (rules[type].length) {
      Forms.updateRequiredFields('contact-form', Validation.resolveRequiredFields(rules[type]));
    } else {
      CommonRepo.getRules(type as any).onSuccess(validationRules => {
        for (const rule of validationRules) {
          if (rule.fieldName === 'firstName') {
            rule.msgOverride = Texts.ERROR_FIRST_NAME;
          } else if (rule.fieldName === 'lastName') {
            rule.msgOverride = Texts.ERROR_LAST_NAME;
          } else if (rule.fieldName === 'email') {
            rule.msgOverride = Texts.ERROR_EMAIL;
          } else if (rule.fieldName === 'message') {
            rule.msgOverride = Texts.ERROR_MESSAGE;
          } else if (rule.fieldName === 'termsAccepted') {
            rule.msgOverride = Texts.ERROR_COMPANY_SIGNUP_TERMS_ACCEPTED;
          } else if (rule.fieldName === 'company') {
            rule.msgOverride = Texts.ERROR_COMPANY_NAME;
          } else if (rule.fieldName === 'phone') {
            rule.msgOverride = Texts.ERROR_PHONE;
          } else if (rule.fieldName === 'country') {
            rule.msgOverride = Texts.ERROR_COUNTRY;
          }
        }
        rules[type] = validationRules;
        Forms.updateRequiredFields('contact-form', Validation.resolveRequiredFields(validationRules));
      });
    }
  }

  function submit(btn: HTMLButtonElement): void {
    const contactInfo: ContactInfo = Forms.read('contact-form');
    contactInfo.termsAccepted = Forms.convertCheckboxVal(contactInfo.termsAccepted);
    const validationErrors = validate(contactInfo);
    if (validationErrors.length) {
      Forms.handle('contact-form', validationErrors);
    } else {
      send(contactInfo, btn);
    }
  }

  function validate(contactInfo: ContactInfo): FieldError[] {
    return Validation.run(contactInfo, rules['CONTACT_' + contactInfo.type]);
  }

  function send(contactInfo: ContactInfo, btn: HTMLButtonElement): void {
    ReCaptcha.getToken('contact', token => {
      Components.execute(btn, Repo.send(contactInfo, token))
        .onSuccess(() => {
          Forms.replaceContent('contact-form', SUCCESS_TEMPLATE);
        })
        .onFailure((_, err) => {
          Forms.handleBackendError('contact-form', err, Texts.ERROR_GENERAL_CONTACT);
        });
    });
  }
}

type ContactInfo = {
  type: 'GENERAL' | 'FREELANCER' | 'COMPANY';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  country: string;
  message: string;
  termsAccepted: boolean;
}

DOM.onReady(Contact.onload);
